import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const userId = searchParams.get("id");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [tokenValid, setTokenValid] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  // Palette de couleurs SecureControl
  const COLORS = {
    primary: '#0B3C5D',     // Bleu Sécurité
    secondary: '#1E88C8',   // Bleu Tech
    accent: '#4FC3F7',      // Bleu Clair
    darkText: '#2E2E2E',    // Gris Foncé
    lightBg: '#F5F7FA',     // Gris Clair
    white: '#FFFFFF',
    danger: '#f44336',
    success: '#4caf50',
    warning: '#ff9800',
    info: '#2196f3'
  };

  // Validation initiale du token
  useEffect(() => {
    if (!token || !userId) {
      setValidating(false);
      setTokenValid(false);
      setError("Lien de réinitialisation invalide ou incomplet.");
      return;
    }

    validateToken();
  }, [token, userId]);

  // Vérifier la force du mot de passe
  useEffect(() => {
    if (!password) {
      setPasswordStrength(0);
      return;
    }

    let strength = 0;
    if (password.length >= 8) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[a-z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;
    
    setPasswordStrength(strength);
  }, [password]);

  const validateToken = async () => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/auth/reset_password.php`,
        {
          action: "validate_token",
          token,
          id: userId
        },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data?.success && response.data?.valid) {
        setTokenValid(true);
        setError("");
      } else {
        setError(response.data?.message || "Lien de réinitialisation invalide ou expiré.");
        setTokenValid(false);
      }
    } catch (err) {
      console.error("Erreur validation token:", err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.code === 'ERR_NETWORK') {
        setError("Erreur réseau. Vérifiez votre connexion.");
      } else {
        setError("Impossible de valider le lien. Veuillez réessayer.");
      }
      setTokenValid(false);
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (!password || password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }

    if (password !== confirm) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/auth/reset_password.php`,
        {
          action: "reset_password",
          token,
          id: userId,
          password,
          confirm_password: confirm
        },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data?.success) {
        setResetSuccess(true);
        setMessage("✅ Mot de passe réinitialisé avec succès !");
        setTimeout(() => {
          navigate("/login", { 
            state: { 
              message: "Votre mot de passe a été réinitialisé avec succès. Vous pouvez maintenant vous connecter.",
              messageType: "success"
            }
          });
        }, 3000);
      } else {
        setError(response.data?.message || "Erreur lors de la réinitialisation.");
      }
    } catch (err) {
      console.error("Erreur reset:", err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.code === 'ERR_NETWORK') {
        setError("Erreur réseau. Vérifiez votre connexion internet.");
      } else if (err.response?.status === 400) {
        setError("Lien de réinitialisation invalide ou expiré.");
      } else {
        setError("Une erreur inattendue s'est produite. Veuillez réessayer.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Indicateur de force du mot de passe
  const getPasswordStrengthLabel = () => {
    if (passwordStrength >= 4) return { text: 'Fort', color: COLORS.success, bg: '#e8f5e9' };
    if (passwordStrength >= 3) return { text: 'Moyen', color: COLORS.warning, bg: '#fff8e1' };
    return { text: 'Faible', color: COLORS.danger, bg: '#ffebee' };
  };

  // État de chargement
  if (validating) {
    return (
      <div style={{ 
        backgroundColor: COLORS.lightBg, 
        height: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Inter', 'Roboto', sans-serif",
        overflow: 'hidden',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      }}>
        <div style={{
          width: '100%',
          maxWidth: '420px',
          padding: '20px',
          transform: 'scale(0.9)',
          transformOrigin: 'center center'
        }}>
          <div className="card p-4 text-center" style={{ 
            backgroundColor: COLORS.white,
            border: 'none',
            borderRadius: '14px',
            boxShadow: '0 6px 24px rgba(11, 60, 93, 0.1)',
            overflow: 'hidden'
          }}>
            <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{ 
              width: '60px',
              height: '60px',
              backgroundColor: '#E3F2FD',
              color: COLORS.primary
            }}>
              <i className="bi bi-shield-lock fs-3"></i>
            </div>
            
            <div className="spinner-border mb-3" style={{ 
              width: '2rem', 
              height: '2rem', 
              color: COLORS.primary 
            }} role="status">
              <span className="visually-hidden">Chargement...</span>
            </div>
            
            <h5 className="mt-3" style={{ 
              color: COLORS.primary, 
              fontWeight: '600',
              marginBottom: '8px',
              fontSize: '1.1rem'
            }}>
              Validation du lien en cours
            </h5>
            
            <p style={{ 
              color: COLORS.darkText,
              opacity: 0.7,
              fontSize: '0.85rem',
              marginBottom: 0
            }}>
              Veuillez patienter pendant que nous vérifions votre lien
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Lien invalide ou token expiré
  if (!tokenValid && !resetSuccess) {
    return (
      <div style={{ 
        backgroundColor: COLORS.lightBg, 
        height: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Inter', 'Roboto', sans-serif",
        overflow: 'hidden',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      }}>
        <div style={{
          width: '100%',
          maxWidth: '450px',
          padding: '20px',
          transform: 'scale(0.9)',
          transformOrigin: 'center center'
        }}>
          {/* Header SecureControl */}
          <div className="text-center mb-4">
            <div className="d-inline-flex align-items-center justify-content-center mb-3" style={{ 
              width: '70px',
              height: '70px',
              backgroundColor: COLORS.primary,
              borderRadius: '18px',
              color: COLORS.white,
              fontSize: '2rem',
              boxShadow: '0 4px 12px rgba(11, 60, 93, 0.2)'
            }}>
              <i className="bi bi-shield-check"></i>
            </div>
            
            <h1 style={{ 
              color: COLORS.primary,
              fontWeight: '700',
              fontSize: '1.8rem',
              marginBottom: '6px',
              lineHeight: '1.2'
            }}>
              SecureControle
            </h1>
            
            <p style={{ 
              color: COLORS.darkText,
              opacity: 0.8,
              fontSize: '0.9rem',
              marginBottom: '0'
            }}>
              Plateforme de gestion de sécurité
            </p>
          </div>

          <div className="card p-4" style={{ 
            backgroundColor: COLORS.white,
            border: 'none',
            borderRadius: '14px',
            boxShadow: '0 6px 24px rgba(11, 60, 93, 0.1)',
            overflow: 'hidden'
          }}>
            <div className="text-center mb-4">
              <div className="rounded-circle d-inline-flex align-items-center justify-content-center mx-auto mb-3" style={{ 
                width: '50px',
                height: '50px',
                backgroundColor: '#ffebee',
                color: COLORS.danger
              }}>
                <i className="bi bi-exclamation-triangle fs-4"></i>
              </div>
              
              <h5 style={{ 
                color: '#c62828',
                fontWeight: '600',
                marginBottom: '12px',
                fontSize: '1.1rem'
              }}>
                Lien de réinitialisation invalide
              </h5>
            </div>
            
            <div className="alert mb-4" style={{ 
              backgroundColor: '#ffebee',
              borderColor: COLORS.danger,
              color: '#c62828',
              borderRadius: '8px',
              borderLeft: `4px solid ${COLORS.danger}`,
              padding: '12px',
              fontSize: '0.85rem'
            }}>
              <div className="d-flex align-items-start">
                <i className="bi bi-exclamation-triangle me-2 mt-1" style={{ fontSize: '1rem' }}></i>
                <div>
                  <strong>Erreur :</strong> {error || "Ce lien est invalide, expiré ou a déjà été utilisé."}
                  <div className="mt-2 small" style={{ opacity: 0.9, fontSize: '0.8rem' }}>
                    Les liens de réinitialisation sont valables 1 heure seulement.
                  </div>
                </div>
              </div>
            </div>
            
            <div className="d-grid gap-3">
              <Link 
                to="/forgot-password"
                className="btn fw-semibold"
                style={{ 
                  backgroundColor: COLORS.primary, 
                  color: COLORS.white, 
                  padding: "12px",
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  transition: 'all 0.3s ease',
                  textDecoration: 'none',
                  border: 'none',
                  height: '48px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = COLORS.secondary;
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 4px 8px rgba(11, 60, 93, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = COLORS.primary;
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <i className="bi bi-arrow-clockwise me-2"></i>
                Demander un nouveau lien
              </Link>
              
              <Link 
                to="/login"
                className="btn fw-semibold"
                style={{ 
                  color: COLORS.primary,
                  border: `1px solid ${COLORS.primary}`,
                  backgroundColor: "transparent", 
                  padding: "12px",
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  transition: 'all 0.3s ease',
                  textDecoration: 'none',
                  height: '44px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = COLORS.primary;
                  e.target.style.color = COLORS.white;
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "transparent";
                  e.target.style.color = COLORS.primary;
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <i className="bi bi-box-arrow-in-right me-2"></i>
                Retour à la connexion
              </Link>
            </div>
            
            <div className="mt-4 pt-3 border-top" style={{ borderColor: '#e0e0e0' }}>
              <small style={{ 
                color: '#6c757d', 
                fontSize: '0.8rem',
                display: 'block',
                textAlign: 'center'
              }}>
                <i className="bi bi-info-circle me-1" style={{ color: COLORS.primary }}></i>
                Besoin d'aide ? Contactez notre support à support@securecontrole.com
              </small>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-3">
            <div style={{ 
              color: COLORS.darkText,
              opacity: 0.6,
              fontSize: '0.8rem',
              lineHeight: '1.4'
            }}>
              <p className="mb-2">
                <span className="d-inline-flex align-items-center justify-content-center" style={{ 
                  backgroundColor: '#E3F2FD',
                  color: COLORS.primary,
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '0.75rem'
                }}>
                  <i className="bi bi-shield-check me-1"></i>
                  Sécurité garantie
                </span>
              </p>
              <p className="mb-0">
                © {new Date().getFullYear()} SecureControle. Tous droits réservés.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Formulaire de réinitialisation OU succès
  return (
    <div style={{ 
      backgroundColor: COLORS.lightBg, 
      height: '100vh',
      width: '100vw',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'Inter', 'Roboto', sans-serif",
      overflow: 'hidden',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0
    }}>
      <div style={{
        width: '100%',
        maxWidth: '450px',
        padding: '20px',
        transform: 'scale(0.9)',
        transformOrigin: 'center center'
      }}>
        {/* Header SecureControl */}
        <div className="text-center mb-4">
          <div className="d-inline-flex align-items-center justify-content-center mb-3" style={{ 
            width: '70px',
            height: '70px',
            backgroundColor: COLORS.primary,
            borderRadius: '18px',
            color: COLORS.white,
            fontSize: '2rem',
            boxShadow: '0 4px 12px rgba(11, 60, 93, 0.2)'
          }}>
            <i className="bi bi-shield-check"></i>
          </div>
          
          <h1 style={{ 
            color: COLORS.primary,
            fontWeight: '700',
            fontSize: '1.8rem',
            marginBottom: '6px',
            lineHeight: '1.2'
          }}>
            SecureControle
          </h1>
          
          <p style={{ 
            color: COLORS.darkText,
            opacity: 0.8,
            fontSize: '0.9rem',
            marginBottom: '0'
          }}>
            Réinitialisation du mot de passe
          </p>
        </div>

        <div className="card p-4" style={{ 
          backgroundColor: COLORS.white,
          border: 'none',
          borderRadius: '14px',
          boxShadow: '0 6px 24px rgba(11, 60, 93, 0.1)',
          overflow: 'hidden'
        }}>
          <div className="text-center mb-4">
            <div className="rounded-circle d-inline-flex align-items-center justify-content-center mx-auto mb-3" style={{ 
              width: '50px',
              height: '50px',
              backgroundColor: '#E3F2FD',
              color: COLORS.primary
            }}>
              <i className={`bi ${resetSuccess ? 'bi-check-circle' : 'bi-key'} fs-4`}></i>
            </div>
            
            <h5 style={{ 
              color: COLORS.primary,
              fontWeight: '600',
              marginBottom: '6px',
              fontSize: '1.1rem'
            }}>
              {resetSuccess ? 'Réinitialisation réussie' : 'Définir un nouveau mot de passe'}
            </h5>
            
            <p style={{ 
              color: COLORS.darkText,
              opacity: 0.7,
              fontSize: '0.85rem',
              marginBottom: 0
            }}>
              {resetSuccess 
                ? "Votre mot de passe a été réinitialisé avec succès !" 
                : "Choisissez un mot de passe sécurisé pour votre compte"}
            </p>
          </div>

          {error && !resetSuccess && (
            <div className="alert mb-4" style={{ 
              backgroundColor: '#ffebee',
              borderColor: COLORS.danger,
              color: '#c62828',
              borderRadius: '8px',
              borderLeft: `4px solid ${COLORS.danger}`,
              padding: '12px',
              fontSize: '0.85rem'
            }}>
              <div className="d-flex align-items-center">
                <button 
                  type="button" 
                  className="btn-close me-2" 
                  onClick={() => setError("")}
                  style={{ fontSize: '0.7rem' }}
                ></button>
                <div>
                  <i className="bi bi-exclamation-triangle me-2" style={{ fontSize: '1rem' }}></i>
                  <span>{error}</span>
                </div>
              </div>
            </div>
          )}

          {message && resetSuccess && (
            <div className="alert mb-4" style={{ 
              backgroundColor: '#e8f5e9',
              borderColor: COLORS.success,
              color: '#2e7d32',
              borderRadius: '8px',
              borderLeft: `4px solid ${COLORS.success}`,
              padding: '12px',
              fontSize: '0.85rem'
            }}>
              <div className="d-flex align-items-center">
                <i className="bi bi-check-circle me-3" style={{ fontSize: '1.2rem' }}></i>
                <div className="flex-grow-1">
                  <strong style={{ fontSize: '1rem', display: 'block', marginBottom: '8px' }}>{message}</strong>
                  <div className="d-flex align-items-center">
                    <div className="spinner-border spinner-border-sm me-2" style={{ 
                      color: '#2e7d32',
                      width: '0.8rem',
                      height: '0.8rem'
                    }}></div>
                    <small style={{ fontSize: '0.8rem' }}>Redirection vers la page de connexion dans 3 secondes...</small>
                  </div>
                </div>
              </div>
              
              <div className="mt-3">
                <Link 
                  to="/login" 
                  className="btn btn-sm fw-semibold"
                  style={{ 
                    backgroundColor: COLORS.primary, 
                    color: COLORS.white,
                    padding: "6px 16px",
                    borderRadius: '6px',
                    transition: 'all 0.3s ease',
                    textDecoration: 'none',
                    border: 'none',
                    fontSize: '0.85rem'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.secondary;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = COLORS.primary;
                  }}
                >
                  <i className="bi bi-box-arrow-in-right me-1"></i>
                  Aller à la connexion maintenant
                </Link>
              </div>
            </div>
          )}

          {!resetSuccess && !message && (
            <form onSubmit={handleSubmit}>
              {/* Mot de passe */}
              <div className="mb-4">
                <label className="form-label d-flex align-items-center" style={{ 
                  color: COLORS.darkText,
                  fontWeight: '500',
                  fontSize: '0.85rem',
                  marginBottom: '6px'
                }}>
                  <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{ 
                    width: '22px',
                    height: '22px',
                    backgroundColor: '#E3F2FD',
                    color: COLORS.primary,
                    fontSize: '0.75rem'
                  }}>
                    <i className="bi bi-key"></i>
                  </div>
                  Nouveau mot de passe <span className="text-danger ms-1">*</span>
                </label>
                
                <div className="input-group">
                  <span className="input-group-text" style={{ 
                    backgroundColor: COLORS.lightBg,
                    borderColor: '#e0e0e0',
                    color: COLORS.secondary,
                    borderRight: 'none',
                    borderRadius: '8px 0 0 8px',
                    padding: '10px 12px',
                    fontSize: '0.9rem'
                  }}>
                    <i className="bi bi-lock"></i>
                  </span>
                  
                  <input
                    type="password"
                    className="form-control"
                    placeholder="Minimum 8 caractères"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    minLength="8"
                    autoFocus
                    style={{ 
                      borderColor: '#e0e0e0',
                      borderLeft: 'none',
                      borderRadius: '0 8px 8px 0',
                      color: COLORS.darkText,
                      padding: '10px 12px',
                      fontSize: '0.9rem',
                      height: '44px'
                    }}
                  />
                </div>
                
                {/* Indicateur de force du mot de passe */}
                {password && (
                  <div className="mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <small style={{ 
                        color: COLORS.darkText, 
                        opacity: 0.7,
                        fontWeight: '500',
                        fontSize: '0.8rem'
                      }}>
                        Force du mot de passe :
                      </small>
                      
                      <small style={{ 
                        color: getPasswordStrengthLabel().color,
                        fontWeight: '600',
                        backgroundColor: getPasswordStrengthLabel().bg,
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '0.7rem'
                      }}>
                        {getPasswordStrengthLabel().text}
                      </small>
                    </div>
                    
                    <div className="progress" style={{ 
                      height: '5px', 
                      borderRadius: '3px',
                      backgroundColor: COLORS.lightBg
                    }}>
                      <div 
                        className="progress-bar" 
                        role="progressbar" 
                        style={{ 
                          width: `${(passwordStrength / 5) * 100}%`,
                          backgroundColor: getPasswordStrengthLabel().color,
                          borderRadius: '3px',
                          transition: 'width 0.3s ease'
                        }}
                      ></div>
                    </div>
                  </div>
                )}
                
                <div className="form-text mt-2" style={{ 
                  color: '#6c757d', 
                  fontSize: '0.8rem',
                  lineHeight: 1.4
                }}>
                  <i className="bi bi-info-circle me-1"></i>
                  Utilisez au moins 8 caractères avec des lettres majuscules/minuscules, chiffres et symboles
                </div>
              </div>

              {/* Confirmation mot de passe */}
              <div className="mb-4">
                <label className="form-label d-flex align-items-center" style={{ 
                  color: COLORS.darkText,
                  fontWeight: '500',
                  fontSize: '0.85rem',
                  marginBottom: '6px'
                }}>
                  <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{ 
                    width: '22px',
                    height: '22px',
                    backgroundColor: '#E3F2FD',
                    color: COLORS.primary,
                    fontSize: '0.75rem'
                  }}>
                    <i className="bi bi-shield-check"></i>
                  </div>
                  Confirmer le mot de passe <span className="text-danger ms-1">*</span>
                </label>
                
                <div className="input-group">
                  <span className="input-group-text" style={{ 
                    backgroundColor: COLORS.lightBg,
                    borderColor: '#e0e0e0',
                    color: COLORS.secondary,
                    borderRight: 'none',
                    borderRadius: '8px 0 0 8px',
                    padding: '10px 12px',
                    fontSize: '0.9rem'
                  }}>
                    <i className="bi bi-lock-fill"></i>
                  </span>
                  
                  <input
                    type="password"
                    className="form-control"
                    placeholder="Répétez le mot de passe"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    disabled={loading}
                    minLength="8"
                    style={{ 
                      borderColor: '#e0e0e0',
                      borderLeft: 'none',
                      borderRadius: '0 8px 8px 0',
                      color: COLORS.darkText,
                      padding: '10px 12px',
                      fontSize: '0.9rem',
                      height: '44px'
                    }}
                  />
                </div>
                
                {confirm && (
                  <div className="mt-2">
                    <small style={{ 
                      color: password === confirm && password.length >= 8 ? COLORS.success : COLORS.danger,
                      fontSize: '0.8rem',
                      fontWeight: '500'
                    }}>
                      <i className={`bi ${password === confirm && password.length >= 8 ? 'bi-check-circle' : 'bi-exclamation-circle'} me-1`}></i>
                      {password === confirm && password.length >= 8 
                        ? 'Les mots de passe correspondent' 
                        : 'Les mots de passe ne correspondent pas'}
                    </small>
                  </div>
                )}
              </div>

              {/* Bouton de soumission */}
              <div className="d-grid mb-4">
                <button
                  className="btn fw-semibold"
                  disabled={loading || password !== confirm || password.length < 8}
                  style={{ 
                    backgroundColor: loading || password !== confirm || password.length < 8 ? '#B0BEC5' : COLORS.primary, 
                    color: COLORS.white,
                    padding: "12px",
                    fontSize: "0.95rem",
                    borderRadius: '8px',
                    transition: "all 0.3s ease",
                    opacity: loading || password !== confirm || password.length < 8 ? 0.8 : 1,
                    border: 'none',
                    height: '48px'
                  }}
                  onMouseEnter={(e) => {
                    if (!(loading || password !== confirm || password.length < 8)) {
                      e.target.style.backgroundColor = COLORS.secondary;
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 4px 8px rgba(11, 60, 93, 0.2)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!(loading || password !== confirm || password.length < 8)) {
                      e.target.style.backgroundColor = COLORS.primary;
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }
                  }}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" 
                        style={{ width: '0.9rem', height: '0.9rem' }}></span>
                      Réinitialisation en cours...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-shield-lock me-2"></i>
                      Réinitialiser mon mot de passe
                    </>
                  )}
                </button>
              </div>

              {/* Ligne séparatrice */}
              <div className="position-relative my-4">
                <hr style={{ 
                  borderColor: '#e0e0e0', 
                  opacity: 0.5,
                  margin: '20px 0'
                }} />
              </div>

              {/* Lien retour connexion */}
              <div className="text-center">
                <Link 
                  to="/login" 
                  className="btn fw-semibold"
                  style={{
                    color: COLORS.primary,
                    border: `1px solid ${COLORS.primary}`,
                    backgroundColor: 'transparent',
                    padding: '10px',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    width: '100%',
                    transition: 'all 0.3s ease',
                    textDecoration: 'none',
                    height: '44px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.primary;
                    e.target.style.color = COLORS.white;
                    e.target.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = COLORS.primary;
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Retour à la connexion
                </Link>
              </div>
            </form>
          )}

          {!resetSuccess && (
            <div className="mt-4 pt-3 border-top" style={{ borderColor: '#e0e0e0' }}>
              <div className="alert" style={{ 
                backgroundColor: '#E3F2FD',
                borderColor: COLORS.accent,
                color: COLORS.primary,
                borderRadius: '8px',
                padding: '10px',
                margin: 0,
                fontSize: '0.8rem'
              }}>
                <div className="d-flex align-items-start">
                  <i className="bi bi-lightbulb me-2 mt-1" style={{ color: COLORS.secondary, fontSize: '0.9rem' }}></i>
                  <div>
                    <strong>Conseil de sécurité :</strong> Utilisez un mot de passe unique que vous n'utilisez nulle part ailleurs.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-3">
          <div style={{ 
            color: COLORS.darkText,
            opacity: 0.6,
            fontSize: '0.8rem',
            lineHeight: '1.4'
          }}>
            <div className="mb-1">
              <span className="d-inline-flex align-items-center mx-1" style={{ 
                backgroundColor: '#E3F2FD',
                color: COLORS.primary,
                padding: '4px 10px',
                borderRadius: '10px',
                fontSize: '0.7rem'
              }}>
                <i className="bi bi-shield-check me-1"></i>
                Sécurité garantie
              </span>
              
              <span className="d-inline-flex align-items-center mx-1" style={{ 
                backgroundColor: '#E3F2FD',
                color: COLORS.primary,
                padding: '4px 10px',
                borderRadius: '10px',
                fontSize: '0.7rem'
              }}>
                <i className="bi bi-cloud-check me-1"></i>
                Service cloud
              </span>
            </div>
            
            <p className="mb-0">
              © {new Date().getFullYear()} SecureControle. Tous droits réservés.
            </p>
          </div>
        </div>
      </div>

      {/* Styles CSS additionnels */}
      <style>{`
        * {
          font-family: 'Inter', 'Roboto', sans-serif;
          box-sizing: border-box;
        }
        
        body, html {
          margin: 0;
          padding: 0;
          overflow: hidden !important;
          height: 100%;
          width: 100%;
          position: fixed;
        }
        
        #root {
          height: 100vh;
          width: 100vw;
          overflow: hidden;
        }
        
        .form-control:focus {
          border-color: ${COLORS.secondary} !important;
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25) !important;
          outline: none;
        }
        
        a {
          text-decoration: none;
        }
        
        .btn:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(11, 60, 93, 0.25) !important;
        }
        
        /* Media Queries pour l'adaptabilité */
        @media (max-width: 768px) {
          div[style*="transform: scale(0.9)"] {
            transform: scale(0.95) !important;
            padding: 16px !important;
          }
          
          .card {
            margin: 0 auto;
            border-radius: 12px !important;
          }
        }
        
        @media (max-width: 576px) {
          div[style*="transform: scale(0.9)"] {
            transform: scale(1) !important;
            padding: 12px !important;
          }
          
          .card-body {
            padding: 20px !important;
          }
          
          .btn {
            padding: 10px !important;
            font-size: 0.9rem !important;
          }
          
          h1 {
            font-size: 1.6rem !important;
          }
          
          .logo-container {
            width: 60px !important;
            height: 60px !important;
            font-size: 1.8rem !important;
          }
        }
        
        @media (max-height: 700px) {
          div[style*="transform: scale(0.9)"] {
            transform: scale(0.85) !important;
          }
          
          .mb-4 {
            margin-bottom: 1rem !important;
          }
          
          .card-body {
            padding: 20px !important;
          }
        }
        
        @media (max-height: 600px) {
          div[style*="transform: scale(0.9)"] {
            transform: scale(0.8) !important;
          }
          
          .card {
            margin-bottom: 10px !important;
          }
          
          .text-center.mb-4 {
            margin-bottom: 1.5rem !important;
          }
        }
        
        @media (max-height: 500px) {
          div[style*="transform: scale(0.9)"] {
            transform: scale(0.75) !important;
          }
        }
        
        /* Pour les très petits écrans */
        @media (max-width: 375px) {
          div[style*="transform: scale(0.9)"] {
            transform: scale(0.95) !important;
          }
          
          .card-body {
            padding: 16px !important;
          }
          
          .input-group-text, .form-control {
            padding: 8px 10px !important;
            font-size: 0.85rem !important;
          }
        }
        
        /* Assure que rien ne dépasse */
        .card, .text-center, form {
          max-width: 100%;
        }
      `}</style>
    </div>
  );
};

export default ResetPassword;